<script>
    import constants from '@/constants';
    import { releaseAllProvider } from '@/api/file.js';
    import { isReadyToRelease } from '@/scripts/providerSearch.mjs';

    export default {
        data() {
            return { menuOpen: false, isReleasing: false, menuPos: {} };
        },
        beforeUnmount() { document.removeEventListener('click', this.closeMenu); },
        computed: {
            canRelease() { return !this.isReleasing && isReadyToRelease(this.game) },
            unreleased() { return this.game.total_files_count - this.game.released_files_count },
            constants() {return constants},
            countColor() {
                if (this.game.released_files_count === 0) {
                    return "count_zero"
                }
                else if (this.game.total_files_count === this.game.released_files_count) {
                    return "count_max"
                }
                else {
                    return "count_no_max";
                }
            }
        },
        props: {
            providerName: String,
            provideID: Number,
            game: Object,
        },
        methods: {
            toggleMenu() {
                this.menuOpen = !this.menuOpen;
                // The list sits in CSS columns, which slice an absolute popup;
                // the menu is teleported to <body> and pinned to the button.
                const r = this.$refs.dots.getBoundingClientRect();
                this.menuPos = { top: `${r.bottom + 4}px`, right: `${window.innerWidth - r.right}px` };
                if (this.menuOpen) {
                    setTimeout(() => document.addEventListener('click', this.closeMenu));
                    // A fixed popup would drift off its button while the page scrolls.
                    window.addEventListener('scroll', this.closeMenu, { once: true });
                } else {
                    document.removeEventListener('click', this.closeMenu);
                }
            },
            closeMenu(e) {
                if (e?.type === 'click' && (this.$refs.menu?.contains(e.target) || this.$refs.dots?.contains(e.target))) return;
                this.menuOpen = false;
                document.removeEventListener('click', this.closeMenu);
            },
            // Release every unreleased file of this one game (869fch4ym).
            async releaseGame() {
                if (!this.canRelease) return;
                this.isReleasing = true;
                try {
                    await this.$store.dispatch('CHECK_ASSET_TYPES');
                    const assetTypeID = this.$store.getters.ASSET_TYPE_IMAGES;
                    if (assetTypeID == null) throw new Error('image asset type is unknown');
                    const released = await releaseAllProvider(this.provideID, assetTypeID, this.game.id);
                    if (released === false) throw new Error('release request failed');
                    this.closeMenu();
                    await this.$store.dispatch('UPDATE_PROVIDER_AND_GAMES');
                } catch (error) {
                    window.alert(`Failed to release files for "${this.game.name}". Please try again.`);
                } finally {
                    this.isReleasing = false;
                }
            },
            async selectProvider() {
                this.$store.commit('SELECT_PROVIDER', this.providerName);
                this.$store.commit('SELECT_GAME', this.game.name);
            }
        }
    }
</script>


<template>
    <li>
        <div class="blur_white cursor_pointer">
            <router-link
                @click="selectProvider"
                :to="{
                    name: constants.ROUTER_NAME.FILEEDIT,
                    query: { 
                        providerID: provideID,
                        gameID: game['id']
                    }
                }"
            >
                <div 
                    class="new_game mr_5 cursor_pointer_label"
                    v-if="game['have_new_files'] === true"
                >
                    <label>NEW</label>
                </div>
                <div
                    :class="`file_count ${countColor} mr_5 cursor_pointer_label`"
                >
                    <label>
                        {{ game['released_files_count'] }} / {{ game['total_files_count'] }}
                    </label>
                </div>
                <label class="cursor_pointer">
                    {{ game['name'] }}
                </label>
            </router-link>
        </div>
        <button
            ref="dots"
            type="button"
            class="game_menu_button"
            :class="{ open: menuOpen }"
            aria-label="Game actions"
            :aria-expanded="menuOpen"
            @click="toggleMenu"
        >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>
        </button>
        <Teleport to="body">
            <div v-if="menuOpen" ref="menu" class="game_menu_list" :style="menuPos">
                <button type="button" class="game_menu_item" :disabled="!canRelease" @click="releaseGame">
                    {{ isReleasing ? 'Releasing…' : 'Release all' }}
                </button>
                <div class="game_menu_hint">
                    {{ canRelease || isReleasing ? `${unreleased} of ${game.total_files_count} files` : 'Nothing to release' }}
                </div>
            </div>
        </Teleport>
    </li>
</template>


<style scoped>
    li { display: flex; align-items: center; }
    .blur_white { flex: 1 1 auto; min-width: 0; }
    .game_menu_button {
        display: flex; align-items: center; justify-content: center;
        width: 26px; height: 26px;
        border: 0; border-radius: 4px;
        background: transparent; color: inherit;
        cursor: pointer; opacity: 0.6;
    }
    .game_menu_button:hover, .game_menu_button.open { opacity: 1; background: rgba(0, 0, 0, 0.08); }
    .game_menu_list {
        position: fixed; z-index: 100;
        min-width: 180px; padding: 6px;
        background: var(--background); color: var(--header-text);
        border: 1px solid var(--header-separator); border-radius: 6px;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    }
    .game_menu_item {
        width: 100%; padding: 8px 10px;
        border: 0; border-radius: 4px;
        background: transparent; color: inherit;
        text-align: left; font: inherit; font-weight: bold; cursor: pointer;
    }
    .game_menu_item:hover:not(:disabled) { background: rgba(0, 0, 0, 0.08); }
    .game_menu_item:disabled { opacity: 0.5; cursor: default; }
    .game_menu_hint { padding: 4px 10px 2px; font-size: 12px; opacity: 0.7; }
</style>